import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

/**
 * Centralized HTTP Error Handler Service
 * Provides consistent error handling and logging across the application.
 * Prioritizes the backend's specific message over generic fallbacks.
 */
@Injectable({
  providedIn: 'root'
})
export class HttpErrorHandlerService {
  /**
   * Handle HTTP errors with consistent logging and error transformation
   * @param operation - Name of the operation that failed
   * @param error - The HTTP error response
   * @returns Observable that emits the error
   */
  handleError(operation: string, error: HttpErrorResponse): Observable<never> {
    let errorMessage = '';

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Error de red: ${error.error.message}`;
      console.error(`[${operation}] Error de cliente:`, error.error.message);
    } else {
      // Backend error
      errorMessage = this.getServerErrorMessage(error);
    }

    // Return error observable with user-friendly message
    return throwError(() => ({
      operation,
      message: errorMessage,
      status: error.status,
      statusText: error.statusText,
      timestamp: new Date().toISOString(),
      originalError: error
    }));
  }

  /**
   * Extract error message from server error response.
   * Prioritizes the backend's specific message over generic fallbacks.
   */
  private getServerErrorMessage(error: HttpErrorResponse): string {
    // Prioritize the backend's specific message when available
    const backendMessage = (error.error as { message?: string } | undefined)
      ?.message;
    if (backendMessage) {
      return backendMessage;
    }

    // Fallback to generic messages by status code
    const fallbackMessages: Record<number, string> = {
      400: 'Solicitud inválida. Por favor verifica los datos enviados.',
      401: 'No autorizado. Por favor inicia sesión nuevamente.',
      403: 'No tienes permisos para realizar esta acción.',
      404: 'Recurso no encontrado.',
      409: 'Conflicto con el estado actual del recurso.',
      422: 'Error de validación. Por favor verifica los datos.',
      500: 'Error interno del servidor. Por favor intenta más tarde.',
      502: 'Error de conexión con el servidor. Por favor intenta más tarde.',
      503: 'Servicio no disponible. Por favor intenta más tarde.',
      504: 'Tiempo de espera agotado. Por favor intenta más tarde.'
    };

    return (
      fallbackMessages[error.status] ??
      error.message ??
      'Error desconocido del servidor.'
    );
  }
}
